import { PROJECT_REGISTRY, canReadProject, assertProjectAllowed, redactProjectForExport, getProjectById, getProjectRegistryVersion, } from "../project-skill-registry/index.js";
import { CwjProjectRuntimeError } from "./types.js";
const DEFAULT_SKILL_ID = "smart-print-audit";
function storageKey(prefix, ownerId, tenantId) {
    return `${prefix}:${tenantId ?? ""}:${ownerId}`;
}
function makeMemoryStorage() {
    const map = new Map();
    return {
        getItem: (key) => map.get(key) ?? null,
        setItem: (key, value) => {
            map.set(key, value);
        },
        removeItem: (key) => {
            map.delete(key);
        },
    };
}
function isBrowserLocalStorage() {
    if (typeof localStorage !== "undefined") {
        return localStorage;
    }
    return undefined;
}
export function createCwjProjectRuntimeStore(options = {}) {
    const storage = options.storage ?? isBrowserLocalStorage() ?? makeMemoryStorage();
    const prefix = options.storageKeyPrefix ?? "smart_shikka_project_runtime";
    const clock = options.clock ?? (() => Date.now());
    const defaultSkillId = options.defaultSkillId ?? DEFAULT_SKILL_ID;
    const contexts = new Map();
    const auditLog = [];
    function cacheKey(ownerId, tenantId) {
        return `${tenantId ?? ""}:${ownerId}`;
    }
    function currentRegistryVersion() {
        return getProjectRegistryVersion();
    }
    async function removePersisted(ownerId, tenantId) {
        const key = storageKey(prefix, ownerId, tenantId);
        if (storage.removeItem) {
            await storage.removeItem(key);
        }
        else {
            await storage.setItem(key, "");
        }
    }
    function persist(context) {
        const snapshot = {
            version: 1,
            projectId: context.projectId,
            skillId: context.skillId,
            ownerId: context.ownerId,
            tenantId: context.tenantId,
            role: context.role,
            selectedAt: context.selectedAt,
            registryVersion: currentRegistryVersion(),
        };
        void storage.setItem(storageKey(prefix, context.ownerId, context.tenantId), JSON.stringify(snapshot));
    }
    function loadPersisted(ownerId, tenantId) {
        const raw = storage.getItem(storageKey(prefix, ownerId, tenantId));
        if (typeof raw !== "string" || !raw)
            return undefined;
        try {
            const parsed = JSON.parse(raw);
            if (typeof parsed === "object" &&
                parsed !== null &&
                parsed.version === 1 &&
                typeof parsed.projectId === "string" &&
                typeof parsed.ownerId === "string" &&
                typeof parsed.role === "string") {
                const snapshot = parsed;
                if (snapshot.registryVersion !== currentRegistryVersion()) {
                    void removePersisted(ownerId, tenantId);
                    void recordAudit("cache_invalidate", { projectId: snapshot.projectId, ownerId, tenantId }, "stale_version");
                    return undefined;
                }
                const project = getProjectById(snapshot.projectId);
                if (!project || project.status !== "active" || !canReadProject(project, tenantId, ownerId)) {
                    void removePersisted(ownerId, tenantId);
                    void recordAudit("cache_invalidate", { projectId: snapshot.projectId, ownerId, tenantId }, "project_disabled_or_unauthorized");
                    return undefined;
                }
                const skillId = project.allowedSkills.includes(snapshot.skillId)
                    ? snapshot.skillId
                    : project.allowedSkills[0] ?? defaultSkillId;
                return {
                    projectId: snapshot.projectId,
                    ownerId: snapshot.ownerId,
                    tenantId: snapshot.tenantId,
                    skillId,
                    role: snapshot.role,
                    selectedAt: snapshot.selectedAt,
                };
            }
        }
        catch {
            void removePersisted(ownerId, tenantId);
        }
        return undefined;
    }
    function recordAudit(type, context, reason) {
        auditLog.push({
            type,
            projectId: context.projectId,
            ownerId: context.ownerId,
            tenantId: context.tenantId,
            timestamp: clock(),
            reason,
        });
    }
    function getCurrentContext(ownerId, tenantId) {
        const key = cacheKey(ownerId, tenantId);
        if (contexts.has(key)) {
            return contexts.get(key);
        }
        const persisted = loadPersisted(ownerId, tenantId);
        if (persisted) {
            contexts.set(key, persisted);
        }
        return persisted;
    }
    function listAllowedProjects(ownerId, tenantId) {
        return PROJECT_REGISTRY.filter((p) => p.status === "active" && canReadProject(p, tenantId, ownerId)).map((p) => redactProjectForExport(p));
    }
    function resolveDefaultSkillId(project, requestedSkillId) {
        if (requestedSkillId) {
            if (!project.allowedSkills.includes(requestedSkillId)) {
                throw new CwjProjectRuntimeError("Skill not allowed for project.", "invalid_skill");
            }
            return requestedSkillId;
        }
        return project.allowedSkills[0] ?? defaultSkillId;
    }
    function selectProject(input) {
        const { projectId, ownerId, tenantId = undefined, skillId = undefined, role = "super-admin" } = input;
        try {
            const project = assertProjectAllowed(projectId, tenantId, ownerId, "read");
            const resolvedSkillId = resolveDefaultSkillId(project, skillId);
            const context = {
                projectId,
                ownerId,
                tenantId,
                skillId: resolvedSkillId,
                role,
                selectedAt: clock(),
            };
            const key = cacheKey(ownerId, tenantId);
            const existing = contexts.get(key);
            contexts.set(key, context);
            persist(context);
            if (existing && existing.projectId !== projectId) {
                recordAudit("project_switched", context, `from ${existing.projectId} to ${projectId}`);
            }
            else if (!existing) {
                recordAudit("project_selected", context, "initial selection");
            }
            return context;
        }
        catch (err) {
            recordAudit("project_denied", { projectId, ownerId, tenantId }, "denied");
            if (err instanceof CwjProjectRuntimeError)
                throw err;
            if (err instanceof Error && err.message.includes("Unknown project")) {
                throw new CwjProjectRuntimeError("Project not found.", "unknown_project");
            }
            if (err instanceof Error && err.message.includes("is disabled")) {
                throw new CwjProjectRuntimeError("Project is disabled.", "disabled_project");
            }
            throw new CwjProjectRuntimeError("Project access denied.", "unauthorized");
        }
    }
    function invalidate(ownerId, tenantId) {
        const key = cacheKey(ownerId, tenantId);
        const existing = contexts.get(key);
        contexts.delete(key);
        if (existing) {
            recordAudit("cache_invalidate", existing, "explicit invalidation");
        }
        void removePersisted(ownerId, tenantId);
    }
    function audit() {
        return [...auditLog];
    }
    return {
        getCurrentContext,
        selectProject,
        listAllowedProjects,
        invalidate,
        audit,
    };
}
//# sourceMappingURL=projectRuntimeStore.js.map